import DOMPurify from 'dompurify';
import { useMemo } from 'react';

interface SanitizedHtmlProps {
  htmlString?: string;
}

const fixRelativeLinks = (html: string): string => {
  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all anchor tags
  const anchors = tempDiv.querySelectorAll('a[href]');

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href');
    if (!href) return;

    const protocolPrefixes = ['http://', 'https://', 'mailto:', 'tel:', '#', '/'];
    const hasProtocol = protocolPrefixes.some((prefix) => href.startsWith(prefix));

    // If it looks like a URL without a protocol, add https://
    if (!hasProtocol && href.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}/)) {
      anchor.setAttribute('href', `https://${href}`);
    }

    // Make all external links open in a new tab (except mailto, tel, and hash links)
    const specialLinks = ['mailto:', 'tel:', '#'];
    const isSpecialLink = specialLinks.some((prefix) => href.startsWith(prefix));

    if (!isSpecialLink) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return tempDiv.innerHTML;
};

// EWM-632: strip focus-stealing attributes from sanitized HTML. Tenant-supplied
// label/description content can render via dangerouslySetInnerHTML; a leftover
// `autofocus` on an injected element re-grabs focus on every parent re-render,
// which was pulling the cursor out of unrelated form fields (Projektbeschreibung →
// AI Advisory document link in LBBW). Stripping the attribute neutralises the
// payload without disallowing the host tags.
const FORBID_ATTR = ['autofocus', 'tabindex'];

export const SanitizedHtml = ({ htmlString }: SanitizedHtmlProps) => {
  const sanitizedHtml = useMemo(() => {
    if (htmlString === undefined) return '';
    const cleaned = DOMPurify.sanitize(htmlString, { FORBID_ATTR });
    return fixRelativeLinks(cleaned);
  }, [htmlString]);

  return (
    <span className='reset-inner-html-style' dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  );
};
